import { ReactNode } from "react"
import { motion } from "framer-motion"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { Skeleton } from "@/components/ui/skeleton"
import { LucideIcon } from "lucide-react"
import { useMediaQuery } from "@/hooks/use-media-query"

interface MetricBoxProps {
  label: string
  isLoading: boolean
  value: ReactNode
  onClick?: () => void
  isClickable?: boolean
}

export function MetricBox({ label, isLoading, value, onClick, isClickable }: MetricBoxProps) {
  const isMobile = useMediaQuery("(max-width: 480px)");
  
  return (
    <motion.div 
      className={cn(
        "p-2.5 sm:p-3 md:p-4 rounded-xl bg-slate-800/80 border border-slate-600/50 hover:border-slate-500 flex flex-col mobile-metric-box transition-all duration-300 hover:bg-slate-800 group",
        isClickable && "cursor-pointer ring-amber-500/50 hover:ring-2"
      )}
      whileHover={{ scale: 1.03, y: -2 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
      onClick={onClick}
    >
      <div className="text-2xs xs:text-xs font-medium text-slate-300 group-hover:text-slate-200 mb-1.5 transition-colors">{label}</div>
      {isLoading ? (
        <Skeleton className="h-5 sm:h-6 md:h-8 w-12 sm:w-16 md:w-20 bg-slate-700 animate-pulse" />
      ) : (
        <div className={cn(
          "text-sm xs:text-base sm:text-lg md:text-xl font-bold tracking-tight truncate mobile-truncate group-hover:scale-105 transition-transform",
          isClickable ? "text-amber-400 underline underline-offset-2" : "text-white"
        )}>
          {value}
        </div>
      )}
    </motion.div>
  )
}

interface ActivityCardProps {
  title: string
  description: string
  icon: LucideIcon
  color: "indigo" | "purple" | "pink"
  spans2Cols?: boolean
  metrics: {
    label1: string
    value1: ReactNode
    label2: string
    value2: ReactNode
    isLoading: boolean
    onValue1Click?: () => void
    value1Clickable?: boolean
  }
  buttonText: string
  onButtonClick: () => void
}

export function ActivityCard({
  title,
  description,
  icon: Icon,
  color,
  spans2Cols = false,
  metrics,
  buttonText,
  onButtonClick,
}: ActivityCardProps) {
  const isMobile = useMediaQuery("(max-width: 480px)");
  
  const colorVariants = {
    indigo: {
      bg: "from-indigo-500/20 to-indigo-600/10",
      border: "border-indigo-500/40",
      iconBg: "bg-indigo-500/20",
      iconBorder: "border-indigo-500/40",
      iconColor: "text-indigo-300",
      textColor: "text-indigo-200",
      buttonBg: "bg-indigo-500/30 hover:bg-indigo-500/40",
      buttonBorder: "border-indigo-500/40",
      buttonText: "text-indigo-200",
      gradientOverlay: "from-indigo-500/0 via-indigo-500/20 to-indigo-500/0",
    },
    purple: {
      bg: "from-purple-500/20 to-purple-600/10",
      border: "border-purple-500/40",
      iconBg: "bg-purple-500/20",
      iconBorder: "border-purple-500/40",
      iconColor: "text-purple-300",
      textColor: "text-purple-200",
      buttonBg: "bg-purple-500/30 hover:bg-purple-500/40",
      buttonBorder: "border-purple-500/40",
      buttonText: "text-purple-200",
      gradientOverlay: "from-purple-500/0 via-purple-500/20 to-purple-500/0",
    },
    pink: {
      bg: "from-pink-500/20 to-pink-600/10",
      border: "border-pink-500/40",
      iconBg: "bg-pink-500/20",
      iconBorder: "border-pink-500/40",
      iconColor: "text-pink-300",
      textColor: "text-pink-200",
      buttonBg: "bg-pink-500/30 hover:bg-pink-500/40",
      buttonBorder: "border-pink-500/40",
      buttonText: "text-pink-200",
      gradientOverlay: "from-pink-500/0 via-pink-500/20 to-pink-500/0",
    },
  }

  const variant = colorVariants[color]

  return (
    <motion.div
      whileHover={isMobile ? {} : { scale: 1.02, y: -4 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      className={cn(
        "relative rounded-xl sm:rounded-2xl h-full w-full overflow-hidden",
        "backdrop-blur-md bg-gradient-to-b border shadow-lg hover:shadow-2xl",
        "transition-all duration-300",
        variant.bg,
        variant.border,
        spans2Cols && "xs:col-span-2",
      )}
    >
      {/* Top gradient overlay for visual interest */}
      <div className={cn(
        "absolute top-0 inset-x-0 h-px bg-gradient-to-r",
        variant.gradientOverlay
      )} />
      
      {/* Animated glow effect on hover */}
      <div className={cn(
        "absolute inset-0 opacity-0 hover:opacity-20 transition-opacity duration-500",
        "bg-gradient-to-br",
        variant.gradientOverlay
      )} />
      
      {/* Content Container */}
      <div className="p-3 sm:p-5 md:p-6 h-full flex flex-col relative z-10">
        {/* Header with icon and title */}
        <div className="flex items-start justify-between mb-3 sm:mb-4 gap-3">
          <div className="flex-1 min-w-0">
            <h3 className={cn(
              "text-base sm:text-lg md:text-xl font-semibold tracking-tight mb-1",
              "text-white truncate" // Improved contrast
            )}>
              {title}
            </h3>
            <p className={cn(
              "text-2xs xs:text-xs sm:text-sm line-clamp-2",
              "text-slate-300" // Improved contrast
            )}>
              {description}
            </p>
          </div>
          
          {/* Icon with pulse animation */}
          <motion.div 
            className={cn(
              "flex items-center justify-center rounded-xl p-2.5 sm:p-3 border flex-shrink-0",
              variant.iconBg,
              variant.iconBorder,
            )}
            whileHover={{ rotate: [0, -10, 10, -10, 0], scale: 1.1 }}
            transition={{ duration: 0.5 }}
          >
            <Icon size={isMobile ? 18 : 20} className={variant.iconColor} />
          </motion.div>
        </div>
        
        {/* Metrics Grid with stagger animation */}
        <motion.div 
          className="grid grid-cols-2 gap-2 sm:gap-3 mt-2 w-full"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.1
              }
            }
          }}
        >
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 10 },
              visible: { opacity: 1, y: 0 }
            }}
          >
            <MetricBox 
              label={metrics.label1}
              value={metrics.value1}
              isLoading={metrics.isLoading}
              onClick={metrics.onValue1Click}
              isClickable={metrics.value1Clickable}
            />
          </motion.div>
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 10 },
              visible: { opacity: 1, y: 0 }
            }}
          >
            <MetricBox 
              label={metrics.label2}
              value={metrics.value2}
              isLoading={metrics.isLoading}
            />
          </motion.div>
        </motion.div>
        
        {/* CTA Button with enhanced interaction */}
        <div className="mt-auto pt-4 sm:pt-5">
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
          >
            <Button
              onClick={onButtonClick}
              className={cn(
                "w-full rounded-xl font-semibold h-10 sm:h-11 px-5 relative overflow-hidden group",
                "shadow-lg hover:shadow-xl transition-all duration-300",
                variant.buttonBg,
                "text-white border-0" // Improved contrast
              )}
            >
              <span className="relative z-10 flex items-center justify-center gap-2">
                {buttonText}
                <svg 
                  className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </span>
              <div className={cn(
                "absolute inset-0 bg-white/10",
                "translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"
              )} />
            </Button>
          </motion.div>
        </div>
      </div>
    </motion.div>
  )
}
